import type { AnalysisResult } from '../domain/models';

const DB_NAME = 'timeline-local-studio';
const DB_VERSION = 1;
const STORE_NAME = 'analysis';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB を開けませんでした'));
  });
}

function withStore<T>(
  mode: IDBTransactionMode,
  callback: (store: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T | undefined> {
  return openDb().then(
    (db) =>
      new Promise<T | undefined>((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, mode);
        const store = transaction.objectStore(STORE_NAME);
        const request = callback(store);

        if (request) {
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error ?? new Error('IndexedDB 操作に失敗しました'));
        } else {
          transaction.oncomplete = () => resolve(undefined);
        }

        transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB トランザクションに失敗しました'));
        transaction.oncomplete = () => db.close();
      }),
  );
}

export async function saveAnalysis(result: AnalysisResult): Promise<void> {
  await withStore('readwrite', (store) => store.put(result));
}

export async function loadLatestAnalysis(): Promise<AnalysisResult | null> {
  const values = await withStore<AnalysisResult[]>('readonly', (store) => store.getAll());
  if (!values || values.length === 0) return null;
  return values.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0];
}

export async function deleteAllAnalysis(): Promise<void> {
  await withStore('readwrite', (store) => store.clear());
}
